import React from "react"
import {Box} from "@mui/material"
import {breakpoints} from "@next/ux-fabric"
import {useMediaQuery} from "../../../hooks/useMediaQuery"
import LowerHeaderWrapper from "../../../components/LowerHeaderWrapper"
import BackToSiteBanner from "../../../components/BackToSiteBanner"
import CookieConsent from "../../../components/CookieConsent"
import WidescreenCookieConsent from "../../../components/WidescreenCookieConsent"
import CountryChangeConfirmPopup from "../../../components/CountryChangeConfirmPopup"
import {
    HeaderContainer,
    IconGroupContainer,
    InnerContainer,
    LeftSideContainer,
    RightSideContainer,
    ShoppingBagWrapper,
} from "./components"
import Brand from "../../../components/Brand"
import {SearchBigScreen, SearchSmallScreen} from "../../../components/Search"
import MyAccount from "../../../components/MyAccount"
import QuickLinksContainer from "../../../components/QuickLinksContainer"
import Favourites from "../../../components/Favourites"
import ShoppingBag from "../../../components/ShoppingBag"
import Checkout from "../../../components/Checkout"
import {HEADER_NAV_BAR_QUICK_LINKS, HEADER_NAV_BAR_SHOPPING_BAG_CHECKOUT} from "../../../config/constants"
import CountryLangSelector from "../../../components/CountryLangSelector"
import TestTools from "../../../components/TestTools"
import TimeMachineDate from "../../../components/TimeMachineDate"
import UpperHeaderWrapper from "../../../components/UpperHeader"
import SaleBagWarningModal from "../../../components/SaleBagWarningModal"
import StaticHeaderBanner from "../../../components/StaticHeaderBanner"
import PrimaryNav from "../../../components/PrimaryNav"
import SecondaryNav from "../../../components/SecondaryNav"
import SecureIcon from "../../../components/SecureIcon"
import HelpLink from "../../../components/SecureHelpLink"
import SecureHeaderWrapper from "../../../components/SecureHeaderWrapper"

export interface ClubHeaderProps {
    closeModalHandler: () => void
    enableCookieConsent: boolean
    showCheckoutButton: boolean
    showModal: boolean
    showSaleWarningBag: boolean
    showStaticHeaderBanner: boolean
    displayTestTools: boolean
    textAlignment: string
    useDevEsi: boolean
    widescreenCookieConsent: boolean
    isSecure: boolean
    xxlBreakpointEnabled: boolean
}

export const ClubHeaderComposition = ({
    closeModalHandler,
    enableCookieConsent,
    showCheckoutButton,
    showModal,
    showSaleWarningBag,
    showStaticHeaderBanner,
    displayTestTools,
    textAlignment,
    widescreenCookieConsent,
    isSecure,
    xxlBreakpointEnabled,
}: ClubHeaderProps) => {
    const isMobile = useMediaQuery(`(max-width:${breakpoints.values.md - 0.25}px)`)

    return isSecure ? (
        <header dir={textAlignment}>
            <SecureHeaderWrapper>
                <LeftSideContainer>
                    <SecureIcon />
                </LeftSideContainer>
                <Brand tabbedNavigationEnabled={false} />
                <RightSideContainer>
                    <HelpLink />
                    <ShoppingBag isSecure />
                    <CountryLangSelector isOpeningFromBottom isSecure />
                </RightSideContainer>
                {enableCookieConsent && (widescreenCookieConsent ? <WidescreenCookieConsent /> : <CookieConsent />)}
            </SecureHeaderWrapper>
        </header>
    ) : (
        <HeaderContainer dir={textAlignment}>
            <BackToSiteBanner />
            <UpperHeaderWrapper>
                {showStaticHeaderBanner && <StaticHeaderBanner />}
                <InnerContainer xxlBreakpointEnabled={xxlBreakpointEnabled}>
                    <LeftSideContainer>
                        {isMobile && (
                            <Box component="div" sx={{display: {xs: "flex"}}}>
                                <MyAccount tooltipEnableModArrow />
                                <Favourites anchorStyles={{display: {xs: "flex"}}} />
                            </Box>
                        )}
                        <SearchBigScreen />
                    </LeftSideContainer>
                    <Brand tabbedNavigationEnabled={false} />
                    <RightSideContainer>
                        <QuickLinksContainer containerStyles={{display: {md: "block", xs: "none"}}} />
                        {!isMobile && (
                            <IconGroupContainer component="div" style={{display: "none"}}>
                                <Favourites />
                                <MyAccount tooltipEnableModArrow />
                            </IconGroupContainer>
                        )}
                        {isMobile && <SearchSmallScreen />}
                        <ShoppingBagWrapper>
                            <ShoppingBag
                                tooltipModifiers={[
                                    {
                                        name: "offset",
                                        options: {
                                            enabled: true,
                                            offset: [0, 7],
                                        },
                                    },
                                ]}
                            />
                        </ShoppingBagWrapper>
                        {showCheckoutButton && (
                            <Box display={{xs: "none", md: "block"}}>
                                <Checkout
                                    dataGaV1={HEADER_NAV_BAR_QUICK_LINKS}
                                    dataGaV2={HEADER_NAV_BAR_SHOPPING_BAG_CHECKOUT}
                                />
                            </Box>
                        )}
                        {displayTestTools && <TestTools />}
                        <TimeMachineDate />
                        {showSaleWarningBag && <SaleBagWarningModal />}
                    </RightSideContainer>
                </InnerContainer>
            </UpperHeaderWrapper>
            <LowerHeaderWrapper>
                <PrimaryNav />
                <SecondaryNav />
            </LowerHeaderWrapper>
            {showModal && <CountryChangeConfirmPopup showModal={showModal} closeModal={closeModalHandler} />}
            {enableCookieConsent && (widescreenCookieConsent ? <WidescreenCookieConsent /> : <CookieConsent />)}
        </HeaderContainer>
    )
}

export default ClubHeaderComposition
